import { Stack, Heading, Flex, Box, Skeleton, Text } from '@chakra-ui/react'
import { SentryErrorComponent } from '@components/Fallback'
import { MemorizedTable } from '@components/GenericTable'
import { FailedToLoad } from '@components/Placeholders/FailedToLoad'
import { useGetMembersQuery } from '@features/organisations'
import { OrgMember } from '@features/organisations/types'
import { AddOrgMember } from '@features/staffMember/components/orgs/AddOrgMember'
import { RemoveOrgMember } from '@features/staffMember/components/orgs/RemoveOrgMember'
import { createFileRoute, useParams } from '@tanstack/react-router'
import { createColumnHelper } from '@tanstack/react-table'
import { useMemo } from 'react'

const columnHelper = createColumnHelper<OrgMember>()

const getTableShape = (orgId: string) => [
  columnHelper.display({
    id: 'user',
    header: 'USER',
    cell: ({ row }) => (
      <Stack>
        <Heading size={'sm'}>
          {row.original.firstName} {row.original.lastName}
        </Heading>
        <Text color={'muted'}>{row.original.emailAddress}</Text>
      </Stack>
    ),
  }),
  columnHelper.accessor('orgRole', { header: 'ROLE' }),
  columnHelper.display({
    id: 'actions',
    header: 'ACTIONS',
    cell: ({ row }) => (
      <RemoveOrgMember orgId={orgId} emailAddress={row.original.emailAddress} />
    ),
  }),
]

const Members = ({ orgId }) => {
  const {
    data: orgUsers,
    isLoading: orgUsersLoading,
    isError: orgUsersError,
  } = useGetMembersQuery(orgId)

  const tableShape = useMemo(() => getTableShape(orgId), [orgId])
  return (
    <Flex justifyContent={'center'}>
      <Box boxSize={'4xl'}>
        <Stack spacing={6}>
          <AddOrgMember orgId={orgId} />
          <Skeleton isLoaded={!orgUsersLoading}>
            {orgUsers && <MemorizedTable data={orgUsers} shape={tableShape} />}
            {orgUsersError && (
              <FailedToLoad message={'Unable to load org members'} />
            )}
          </Skeleton>
        </Stack>
      </Box>
    </Flex>
  )
}

const OrgMembersRoute = () => {
  const { orgId } = useParams({ from: '/_auth/staff/o/$orgId/members' })

  return <Members orgId={orgId} />
}

export const Route = createFileRoute('/_auth/staff/o/$orgId/members')({
  component: OrgMembersRoute,
  beforeLoad: () => ({ getTitle: () => 'Org Members' }),
  errorComponent: SentryErrorComponent,
  loader: () => ({
    crumb: 'Members',
  }),
})
